import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Badge, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import 'moment/locale/it';
import { rollCallService } from '../../services/api';
import './DailyRollCallsCalendar.css';



// Custom button component for DatePicker trigger
const DateTriggerButton = React.forwardRef(({ value, onClick, formattedText }, ref) => (
  <Button
    variant="outline-secondary"
    size="sm"
    className="daily-date-picker-btn d-flex align-items-center gap-2 fw-semibold shadow-sm"
    onClick={onClick}
    ref={ref}
    title="Clicca per scegliere una data dal calendario"
    data-testid="daily-date-trigger-btn"
  >
    <i className="fas fa-calendar-alt text-primary"></i>
    <span className="text-capitalize">{formattedText}</span>
  </Button>
));
DateTriggerButton.displayName = 'DateTriggerButton';

const DailyRollCallsCalendar = ({ initialDate = null }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  const formattedDate = useMemo(() => {
    return moment(selectedDate).locale('it').format('dddd D MMMM YYYY');
  }, [selectedDate]);

  const isToday = useMemo(() => {
    return moment(selectedDate).isSame(moment(), 'day');
  }, [selectedDate]);

  const dateParamString = useMemo(() => {
    return moment(selectedDate).format('YYYY-MM-DD');
  }, [selectedDate]);

  const loadData = useCallback(async (date) => {
    try {
      setLoading(true);
      setError('');
      const dateString = moment(date).format('YYYY-MM-DD');
      const response = await rollCallService.getDailyRollCalls(dateString);
      if (response.success && Array.isArray(response.data)) {
        setDailyData(response.data);
      } else if (response.success) {
        setDailyData([]);
      } else {
        setError(response.message || 'Errore nel caricamento degli appelli del giorno');
      }
    } catch (err) {
      console.error('Error loading daily roll calls:', err);
      setError('Errore di connessione durante il recupero degli appelli');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(selectedDate);
  }, [selectedDate, loadData]);

  const handlePrevDay = () => {
    setSelectedDate(prev => moment(prev).subtract(1, 'day').toDate());
  };

  const handleNextDay = () => {
    setSelectedDate(prev => moment(prev).add(1, 'day').toDate());
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleGoToRollCall = (groupId) => {
    navigate(`/groups/${groupId}/rollcall?date=${dateParamString}`);
  };

  return (
    <Card className="daily-roll-calls-card mb-4" data-testid="daily-roll-calls-calendar">
      {/* Header con titolo e navigatore di data */}
      <Card.Header className="daily-roll-calls-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          <i className="fas fa-calendar-check text-primary fs-5"></i>
          <h5 className="mb-0 fw-bold text-dark">Appelli del Giorno</h5>
          {dailyData.length > 0 && (
            <Badge bg="primary" pill className="ms-2">
              {dailyData.length} {dailyData.length === 1 ? 'gruppo' : 'gruppi'}
            </Badge>
          )}
        </div>

        {/* Controlli data: < | Oggi | > | DatePicker popup */}
        <div className="daily-date-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handlePrevDay}
            title="Giorno precedente"
            aria-label="Giorno precedente"
            data-testid="prev-day-btn"
          >
            <i className="fas fa-chevron-left"></i>
          </Button>

          <Button
            variant={isToday ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={handleToday}
            disabled={isToday}
            title="Vai alla data di oggi"
            data-testid="today-btn"
            className="fw-semibold"
          >
            Oggi
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleNextDay}
            title="Giorno successivo"
            aria-label="Giorno successivo"
            data-testid="next-day-btn"
          >
            <i className="fas fa-chevron-right"></i>
          </Button>

          <div className="daily-date-picker-wrapper">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              dateFormat="dd/MM/yyyy"
              customInput={<DateTriggerButton formattedText={formattedDate} />}
            />
          </div>
        </div>
      </Card.Header>

      {/* Contenuto principale */}
      <Card.Body className="p-3">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5" data-testid="daily-roll-calls-loading">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Caricamento appelli del giorno...</span>
            </Spinner>
            <div className="mt-2 text-muted small">Caricamento appelli in corso...</div>
          </div>
        ) : !Array.isArray(dailyData) || dailyData.length === 0 ? (
          /* Stato vuoto */
          <div className="text-center py-5 text-muted" data-testid="daily-roll-calls-empty">
            <i className="fas fa-clipboard-list fa-3x mb-3 text-secondary opacity-50"></i>
            <h6 className="fw-semibold text-dark mb-1">Nessun appello registrato</h6>
            <p className="small mb-0">
              Non risultano appelli effettuati nella giornata di{' '}
              <strong className="text-capitalize">{formattedDate}</strong>.
            </p>
          </div>
        ) : (
          /* Lista dei gruppi con appello registrato */
          <div className="d-flex flex-column gap-3" data-testid="daily-roll-calls-list">
            {dailyData.map((item) => {
              const isExpanded = !!expandedGroups[item.groupId];
              const attendanceRate = item.totalParticipants > 0
                ? Math.round((item.presentCount / item.totalParticipants) * 100)
                : 0;

              return (
                <div key={item.groupId} className="daily-group-card shadow-sm" data-testid={`group-card-${item.groupId}`}>
                  <div className="daily-group-header">
                    <Row className="align-items-center g-3">
                      {/* Nome gruppo e corso */}
                      <Col lg={4} md={6} xs={12}>
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-dark fs-6">{item.groupName}</span>
                          </div>
                          <div className="text-muted small mt-1">
                            <i className="fas fa-graduation-cap me-1 text-primary"></i>
                            {item.courseName}{' '}
                            <span className="text-muted opacity-75">
                              ({item.academicYear}/{item.academicYear + 1})
                            </span>
                          </div>
                          {item.lastUpdatedAt && (
                            <div className="text-muted small mt-1" style={{ fontSize: '0.78rem' }}>
                              <i className="fas fa-clock me-1 text-secondary"></i>
                              Registrato alle {moment(item.lastUpdatedAt).format('HH:mm')}
                            </div>
                          )}
                        </div>
                      </Col>

                      {/* Statistiche presenze & progress bar */}
                      <Col lg={4} md={6} xs={12}>
                        <div className="daily-progress-container">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small fw-semibold text-dark">
                              Presenze: {item.presentCount} / {item.totalParticipants}
                            </span>
                            <Badge bg={attendanceRate >= 70 ? 'success' : attendanceRate >= 40 ? 'warning' : 'danger'}>
                              {attendanceRate}%
                            </Badge>
                          </div>
                          <ProgressBar
                            now={attendanceRate}
                            variant={attendanceRate >= 70 ? 'success' : attendanceRate >= 40 ? 'warning' : 'danger'}
                            style={{ height: '8px' }}
                            className="rounded-pill"
                          />
                          <div className="d-flex gap-2 mt-2">
                            <Badge bg="success-subtle" className="text-success border border-success-subtle px-2 py-1">
                              <i className="fas fa-check-circle me-1"></i>
                              {item.presentCount} Presenti
                            </Badge>
                            <Badge bg="danger-subtle" className="text-danger border border-danger-subtle px-2 py-1">
                              <i className="fas fa-times-circle me-1"></i>
                              {item.absentCount} Assenti
                            </Badge>
                          </div>
                        </div>
                      </Col>

                      {/* Azioni: Espandi presenze + Gestisci appello */}
                      <Col lg={4} xs={12} className="d-flex justify-content-lg-end justify-content-start gap-2 align-items-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => toggleGroup(item.groupId)}
                          className="d-flex align-items-center gap-1"
                          aria-expanded={isExpanded}
                          data-testid={`toggle-group-${item.groupId}`}
                        >
                          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} me-1`}></i>
                          {isExpanded ? 'Nascondi Dettaglio' : `Dettaglio (${item.attendees?.length || 0})`}
                        </Button>

                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleGoToRollCall(item.groupId)}
                          className="d-flex align-items-center gap-1"
                          data-testid={`go-to-rollcall-${item.groupId}`}
                        >
                          <i className="fas fa-clipboard-check me-1"></i>
                          Gestisci Appello
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {/* Dettaglio espandibile con elenco atleti presenti/assenti */}
                  {isExpanded && (
                    <div className="daily-attendees-list" data-testid={`attendees-list-${item.groupId}`}>
                      <h6 className="fw-bold text-dark small mb-3">
                        <i className="fas fa-user-friends me-1 text-primary"></i>
                        Elenco Partecipanti ({item.attendees?.length || 0})
                      </h6>
                      {(!item.attendees || item.attendees.length === 0) ? (
                        <div className="text-muted small fst-italic">Nessun dettaglio disponibile.</div>
                      ) : (
                        <Row className="g-2">
                          {item.attendees.map((attendee) => (
                            <Col md={6} lg={4} key={attendee.userId}>
                              <div className="daily-attendee-item">
                                <div className="d-flex align-items-center text-truncate me-2">
                                  <i
                                    className={`fas fa-user-circle me-2 ${
                                      attendee.isPresent ? 'text-success' : 'text-danger opacity-75'
                                    }`}
                                  ></i>
                                  <span className="small fw-semibold text-truncate">
                                    {attendee.lastName} {attendee.firstName}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                  <Badge bg={attendee.isPresent ? 'success' : 'danger'}>
                                    {attendee.isPresent ? 'Presente' : 'Assente'}
                                  </Badge>
                                  {attendee.notes && (
                                    <span
                                      className="badge bg-secondary text-white"
                                      title={attendee.notes}
                                      style={{ cursor: 'help' }}
                                    >
                                      <i className="fas fa-sticky-note"></i>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DailyRollCallsCalendar;
